import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Loader2, RefreshCw } from 'lucide-react';
import { authApi } from '../../services/api';
import { useSettings } from '../../context/SettingsContext';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const PAGE = 50;
const LABELS = {
  'security.login_failed': 'Failed sign-in',
  'security.login_burst': 'Repeated failed sign-ins',
  'security.rate_limited': 'Rate limit hit',
  'security.request_blocked': 'Suspicious request blocked',
  'security.role_changed': 'Role changed',
  'security.password_reset_requested': 'Password reset requested',
  'security.password_reset': 'Password reset',
  'security.password_changed': 'Password changed',
  'security.sessions_revoked': 'Other sessions signed out',
  'security.session_timeout': 'Signed out after inactivity',
};
const SEV = {
  critical: { bg: '#FEF2F2', border: '#F6B4AC', text: '#991B1B', label: 'Critical' },
  warning: { bg: '#FFFBEB', border: '#F2D97A', text: '#92600A', label: 'Warning' },
  info: { bg: '#F4F2EE', border: '#DEDAD1', text: '#6B665D', label: 'Info' },
};

const describe = (r) => {
  const d = r.details || {};
  if (r.action === 'security.role_changed') return `${d.target_email}: ${d.from} → ${d.to}`;
  if (r.action === 'security.login_failed') return ({ bad_password: 'Wrong password', unknown_email: 'Unknown email', deactivated: 'Deactivated account' })[d.reason] || d.reason;
  if (r.action === 'security.login_burst') return `${d.count} attempts in ${d.window_minutes} min`;
  if (d.path) return d.path;
  return '';
};

const SevPill = ({ severity }) => {
  const s = SEV[severity] || SEV.info;
  return <span className="text-[10px] font-bold uppercase tracking-wider rounded-sm border px-1.5 py-0.5" style={{ background: s.bg, borderColor: s.border, color: s.text }}>{s.label}</span>;
};

export const SecurityActivityCard = () => {
  const { formatDateTime } = useSettings();
  const [category, setCategory] = useState('all');
  const [severity, setSeverity] = useState('all');
  const [data, setData] = useState({ items: [], total: 0, hotspots: [] });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (offset = 0) => {
    setLoading(true);
    try {
      const params = { limit: PAGE, offset };
      if (category !== 'all') params.category = category;
      if (severity !== 'all') params.severity = severity;
      const { data: res } = await authApi.securityActivity(params);
      setData(prev => offset ? { ...res, items: [...prev.items, ...res.items] } : res);
    } catch { toast.error('Could not load security activity'); } finally { setLoading(false); }
  }, [category, severity]);

  useEffect(() => { load(0); }, [load]);

  return (
    <div className="bg-white rounded-xl border border-[#DAD5CA] p-4 space-y-4" data-testid="security-activity-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-bold text-[15px]">Security activity</h3>
          <p className="text-xs text-muted-foreground">Failed sign-ins, blocked requests, role changes, password and session events in your workspace.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-44 h-8" data-testid="security-filter-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All events</SelectItem>
              <SelectItem value="failed_login">Failed sign-ins</SelectItem>
              <SelectItem value="blocked">Blocked requests</SelectItem>
              <SelectItem value="role_change">Role changes</SelectItem>
              <SelectItem value="password">Passwords</SelectItem>
              <SelectItem value="session">Sessions</SelectItem>
            </SelectContent>
          </Select>
          <Select value={severity} onValueChange={setSeverity}>
            <SelectTrigger className="w-32 h-8" data-testid="security-filter-severity"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any severity</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
              <SelectItem value="warning">Warning</SelectItem>
              <SelectItem value="info">Info</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" className="h-8" onClick={() => load(0)} data-testid="security-refresh"><RefreshCw className="w-3.5 h-3.5" /></Button>
        </div>
      </div>

      {data.hotspots?.map(h => (
        <div key={h.email} className="flex items-center gap-2 text-xs rounded border px-3 py-2" style={{ background: '#FEF2F2', borderColor: '#F6B4AC', color: '#991B1B' }} data-testid="security-hotspot">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span><strong>{h.count} failed sign-ins</strong> for {h.email || 'unknown'} in the last hour (latest {formatDateTime(h.last_at)})</span>
        </div>
      ))}

      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]" data-testid="security-activity-table">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground border-b">
              <th className="py-2 pr-3">When</th><th className="py-2 pr-3">Event</th><th className="py-2 pr-3">Account</th>
              <th className="py-2 pr-3">Detail</th><th className="py-2 pr-3">IP</th><th className="py-2">Severity</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map(r => (
              <tr key={r.id} className="border-b last:border-0" data-testid={`security-row-${r.id}`}>
                <td className="py-2 pr-3 whitespace-nowrap font-mono text-[11.5px]">{formatDateTime(r.created_at)}</td>
                <td className="py-2 pr-3 font-semibold">{LABELS[r.action] || r.action}</td>
                <td className="py-2 pr-3">{r.user_email || '—'}</td>
                <td className="py-2 pr-3 text-muted-foreground">{describe(r)}</td>
                <td className="py-2 pr-3 font-mono text-[11.5px]">{r.ip_address || r.details?.ip || '—'}</td>
                <td className="py-2"><SevPill severity={r.severity} /></td>
              </tr>
            ))}
            {!loading && data.items.length === 0 && (
              <tr><td colSpan={6} className="py-6 text-center text-muted-foreground" data-testid="security-empty">No security events yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {loading && <div className="flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>}
      {!loading && data.items.length < data.total && (
        <div className="flex justify-center">
          <Button variant="outline" size="sm" onClick={() => load(data.items.length)} data-testid="security-load-more">Load more ({data.total - data.items.length})</Button>
        </div>
      )}
    </div>
  );
};
